import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, buildQueryString } from '@/lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

export type AlertSeverity = 'low' | 'medium' | 'high' | 'critical'

export interface EmailSettings {
  senderName: string
  senderAddress: string
  briefingSendTime: string // HH:MM UTC
  briefingMaxItems: number
  briefingContentTypes: string[]
  alertEnabled: boolean
  alertMinSeverity: AlertSeverity
  updatedAt: string
  updatedBy: string | null
}

export interface EmailSettingsResponse {
  settings: EmailSettings
}

export interface UpdateEmailSettingsInput {
  senderName?: string
  senderAddress?: string
  briefingSendTime?: string
  briefingMaxItems?: number
  briefingContentTypes?: string[]
  alertEnabled?: boolean
  alertMinSeverity?: AlertSeverity
}

/**
 * A historical row of the legacy `email_events` table (no longer written since
 * KAN-692), sanitised by the API: no recipient address, no provider message id.
 */
export interface EmailEvent {
  id: string
  eventType: string
  occurredAt: string
  createdAt: string
}

/** KAN-842 — a Resend event attributed to a send-ledger attempt (no recipient, no provider id). */
export interface ProviderEmailEvent {
  id: string
  eventType: string // email.delivered | email.delivery_delayed | email.bounced | email.complained | email.suppressed | email.failed
  detailClass: string | null
  sendKind: string
  occurredAt: string
  receivedAt: string
}

export interface EmailEventsResponse {
  source: 'email_provider_events'
  /**
   * ledger_off: the send ledger has recorded no send, so no event can be attributed.
   * awaiting_events: sends recorded, no provider event stored yet. live: events stored.
   */
  status: 'ledger_off' | 'awaiting_events' | 'live'
  ledger: { attempts: number; attemptsWithProviderId: number }
  counts: Record<string, number>
  countsWindowDays: number
  events: ProviderEmailEvent[]
  legacyEvents: EmailEvent[]
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Fetch platform-wide email configuration (singleton row). */
export function useEmailSettings() {
  return useQuery({
    queryKey: ['admin', 'email', 'settings'],
    queryFn: () => api.get<EmailSettingsResponse>('/admin/email/settings'),
    staleTime: 30_000,
  })
}

/** Partial update of email settings. Server returns the updated row. */
export function useUpdateEmailSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateEmailSettingsInput) =>
      api.patch<EmailSettingsResponse>('/admin/email/settings', input),
    onSuccess: (data) => {
      // Push the fresh row straight into the cache so the page doesn't flicker.
      qc.setQueryData(['admin', 'email', 'settings'], data)
    },
  })
}

/** Fetch recent Resend delivery events from the send ledger (default limit 50, max 500). */
export function useEmailEvents(limit = 50) {
  const qs = buildQueryString({ limit })
  return useQuery({
    queryKey: ['admin', 'email', 'events', limit],
    queryFn: () => api.get<EmailEventsResponse>(`/admin/email/events${qs}`),
    staleTime: 10_000,
  })
}
